var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
var __spreadArray = this && this.__spreadArray || function (to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx } from "react/jsx-runtime";
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelectCurrency } from '../../annual/selectors';
import { PeriodSectionBody } from '../common/periodSectionBody';
import { PeriodSectionTable, PeriodSectionWithTotalTable } from '../common/periodSectionTable';
import { getLobExpansionKey, getMultiskillExpansionKeys, getMultiskillGroupExpansionKeys, getMultiskillSectionExpansionKey, getQueueExpansionKeysForLob } from '../common/queueRow';
import { SUPPORT_STAFF_SALARIES_ROWS } from '../constants';
var useQueueExpansionItems = function (data, keyItem, role) {
  return React.useMemo(function () {
    var _a, _b;
    return (_b = (_a = data.queuesByLob) === null || _a === void 0 ? void 0 : _a.flatMap(function (lob) {
      return getQueueExpansionKeysForLob(lob, keyItem, role);
    })) !== null && _b !== void 0 ? _b : [];
  }, [data.queuesByLob, keyItem, role]);
};
var useLobExpansionItems = function (data, keyItem, role) {
  return React.useMemo(function () {
    var _a, _b;
    return (_b = (_a = data.queuesByLob) === null || _a === void 0 ? void 0 : _a.map(function (lob) {
      return getLobExpansionKey(lob.lobName, keyItem, role);
    })) !== null && _b !== void 0 ? _b : [];
  }, [data.queuesByLob, keyItem, role]);
};
var useLobNestedExpansionItems = function (data, keyItem, role) {
  return React.useCallback(function (itemName) {
    var _a;
    var lobEntry = (_a = data.queuesByLob) === null || _a === void 0 ? void 0 : _a.find(function (lob) {
      return getLobExpansionKey(lob.lobName, keyItem, role) === itemName;
    });
    return lobEntry ? getQueueExpansionKeysForLob(lobEntry, keyItem, role) : [];
  }, [data.queuesByLob, keyItem, role]);
};
var useFinancialNestedExpansion = function (data, keyItem) {
  var queueExpansionItems = useQueueExpansionItems(data, keyItem);
  var lobExpansionItems = useLobExpansionItems(data, keyItem);
  var multiskillExpansionItems = React.useMemo(function () {
    return getMultiskillExpansionKeys(data.queues, keyItem);
  }, [data.queues, keyItem]);
  var multiskillGroupExpansionItems = React.useMemo(function () {
    return getMultiskillGroupExpansionKeys(data.queues, keyItem);
  }, [data.queues, keyItem]);
  var getLobNestedExpansionItems = useLobNestedExpansionItems(data, keyItem);
  var getNestedExpansionItems = React.useCallback(function (itemName) {
    if (itemName === keyItem) return __spreadArray(__spreadArray(__spreadArray([], lobExpansionItems, true), queueExpansionItems, true), multiskillExpansionItems, true);
    if (itemName === getMultiskillSectionExpansionKey(keyItem)) return multiskillGroupExpansionItems;
    return getLobNestedExpansionItems(itemName);
  }, [getLobNestedExpansionItems, keyItem, lobExpansionItems, multiskillExpansionItems, multiskillGroupExpansionItems, queueExpansionItems]);
  var expansionItems = React.useMemo(function () {
    return __spreadArray(__spreadArray(__spreadArray([keyItem], lobExpansionItems, true), queueExpansionItems, true), multiskillExpansionItems, true);
  }, [keyItem, lobExpansionItems, multiskillExpansionItems, queueExpansionItems]);
  return {
    expansionItems: expansionItems,
    getNestedExpansionItems: getNestedExpansionItems
  };
};
var TotalBudgetBody = function (props) {
  var t = useTranslation('report', {
    keyPrefix: 'period'
  }).t;
  return _jsx(PeriodSectionWithTotalTable, __assign({}, props, {
    valueKey: "totalBudgets",
    title: t('reportRows.totalBudgets'),
    totalAsParent: true
  }));
};
var AgentSalariesBody = function (props) {
  var t = useTranslation('report', {
    keyPrefix: 'period'
  }).t;
  return _jsx(PeriodSectionWithTotalTable, __assign({}, props, {
    valueKey: "agentsSalaries",
    title: t('reportRows.totalAgentsSalaries'),
    totalAsParent: true
  }));
};
var HiringAheadBody = function (props) {
  var t = useTranslation('report', {
    keyPrefix: 'period'
  }).t;
  return _jsx(PeriodSectionWithTotalTable, __assign({}, props, {
    valueKey: "hiringAhead",
    title: t('reportRows.totalHiringAhead'),
    totalAsParent: true
  }));
};
var OvertimeCostsBody = function (props) {
  var t = useTranslation('report', {
    keyPrefix: 'period'
  }).t;
  return _jsx(PeriodSectionWithTotalTable, __assign({}, props, {
    valueKey: "overtimeCosts",
    title: t('reportRows.totalOvertimeCosts'),
    totalAsParent: true
  }));
};
// Cost per work activity is a per-unit rate, not an additive amount, so the Total column stays but is left blank.
var CostPerWorkBody = function (props) {
  return _jsx(PeriodSectionWithTotalTable, __assign({}, props, {
    valueKey: "costPerWork",
    blankTotal: true
  }));
};
var SupportStaffSalariesBody = function (props) {
  return _jsx(PeriodSectionBody, __assign({}, props, {
    adjustItem: "totalSupportStaffSalaries",
    extendItem: "supportStaffSalaries",
    rowItem: SUPPORT_STAFF_SALARIES_ROWS,
    totalExtendAsParent: true
  }));
};
export var StaffBudgetTable = function (_a) {
  var data = _a.data,
    index = _a.index,
    addColumns = _a.addColumns,
    isProdHoursPlan = _a.isProdHoursPlan,
    scenarioSelector = _a.scenarioSelector;
  var t = useTranslation('report', {
    keyPrefix: 'period'
  }).t;
  var _b = useFinancialNestedExpansion(data, 'totalBudgets'),
    expansionItems = _b.expansionItems,
    getNestedExpansionItems = _b.getNestedExpansionItems;
  var lobExpansionItems = useLobExpansionItems(data, 'totalBudgets');
  var multiskillDefaultExpandedItems = React.useMemo(function () {
    return getMultiskillGroupExpansionKeys(data.queues, 'totalBudgets').length ? [getMultiskillSectionExpansionKey('totalBudgets')] : [];
  }, [data.queues]);
  var currency = useSelectCurrency();
  return _jsx(PeriodSectionTable, {
    title: t('staffBudget', {
      currency: currency
    }),
    data: data,
    index: index,
    addColumns: addColumns,
    isProdHoursPlan: isProdHoursPlan,
    expansionItems: expansionItems,
    defaultExpandedItems: __spreadArray(__spreadArray(['totalBudgets'], lobExpansionItems, true), multiskillDefaultExpandedItems, true),
    firstHeader: true,
    tableBodyComponent: TotalBudgetBody,
    scenarioSelector: scenarioSelector,
    getNestedExpansionItems: getNestedExpansionItems
  });
};
export var AgentSalariesTable = function (_a) {
  var data = _a.data,
    index = _a.index,
    addColumns = _a.addColumns,
    isProdHoursPlan = _a.isProdHoursPlan;
  var t = useTranslation('report', {
    keyPrefix: 'period'
  }).t;
  var _b = useFinancialNestedExpansion(data, 'agentsSalaries'),
    expansionItems = _b.expansionItems,
    getNestedExpansionItems = _b.getNestedExpansionItems;
  var currency = useSelectCurrency();
  return _jsx(PeriodSectionTable, {
    title: t('agentsSalaries', {
      currency: currency
    }),
    data: data,
    index: index,
    addColumns: addColumns,
    isProdHoursPlan: isProdHoursPlan,
    expansionItems: expansionItems,
    defaultExpanded: false,
    tableBodyComponent: AgentSalariesBody,
    getNestedExpansionItems: getNestedExpansionItems
  });
};
export var SupportStaffSalariesTable = function (_a) {
  var data = _a.data,
    index = _a.index,
    addColumns = _a.addColumns,
    isProdHoursPlan = _a.isProdHoursPlan;
  var t = useTranslation('report', {
    keyPrefix: 'period'
  }).t;
  var roles = React.useMemo(function () {
    var _a;
    return Object.keys((_a = data === null || data === void 0 ? void 0 : data.supportStaffSalaries) !== null && _a !== void 0 ? _a : {});
  }, [data]);
  var lobs = React.useMemo(function () {
    var _a;
    return (_a = data.queuesByLob) !== null && _a !== void 0 ? _a : [];
  }, [data.queuesByLob]);
  var getRoleNestedExpansionItems = React.useCallback(function (role) {
    return lobs.flatMap(function (lob) {
      return __spreadArray([getLobExpansionKey(lob.lobName, 'supportStaffSalaries', role)], getQueueExpansionKeysForLob(lob, 'supportStaffSalaries', role), true);
    }).concat(getMultiskillExpansionKeys(data.queues, 'supportStaffSalaries', role));
  }, [data.queues, lobs]);
  var getNestedExpansionItems = React.useCallback(function (itemName) {
    if (itemName === 'totalSupportStaffSalaries') {
      return __spreadArray(__spreadArray(__spreadArray([], SUPPORT_STAFF_SALARIES_ROWS, true), roles, true), roles.flatMap(getRoleNestedExpansionItems), true);
    }
    if (SUPPORT_STAFF_SALARIES_ROWS.includes(itemName)) {
      return __spreadArray(__spreadArray([], roles, true), roles.flatMap(getRoleNestedExpansionItems), true);
    }
    if (roles.includes(itemName)) return getRoleNestedExpansionItems(itemName);
    var multiskillRole = roles.find(function (roleName) {
      return itemName === getMultiskillSectionExpansionKey('supportStaffSalaries', roleName);
    });
    if (multiskillRole) return getMultiskillGroupExpansionKeys(data.queues, 'supportStaffSalaries', multiskillRole);
    var role = roles.find(function (roleName) {
      return itemName.startsWith("supportStaffSalaries::".concat(roleName, "::"));
    });
    if (!role) return [];
    var lobEntry = lobs.find(function (lob) {
      return itemName === getLobExpansionKey(lob.lobName, 'supportStaffSalaries', role);
    });
    if (!lobEntry) return [];
    return getQueueExpansionKeysForLob(lobEntry, 'supportStaffSalaries', role);
  }, [data.queues, getRoleNestedExpansionItems, lobs, roles]);
  var expansionItems = React.useMemo(function () {
    return __spreadArray(__spreadArray(__spreadArray(['totalSupportStaffSalaries'], SUPPORT_STAFF_SALARIES_ROWS, true), roles, true), roles.flatMap(getRoleNestedExpansionItems), true);
  }, [getRoleNestedExpansionItems, roles]);
  var currency = useSelectCurrency();
  return _jsx(PeriodSectionTable, {
    title: t('supportStaffSalaries', {
      currency: currency
    }),
    data: data,
    index: index,
    addColumns: addColumns,
    isProdHoursPlan: isProdHoursPlan,
    expansionItems: expansionItems,
    defaultExpandedItems: ['totalSupportStaffSalaries'],
    tableBodyComponent: SupportStaffSalariesBody,
    getNestedExpansionItems: getNestedExpansionItems
  });
};
export var HiringAheadOfAttritionTable = function (_a) {
  var data = _a.data,
    index = _a.index,
    addColumns = _a.addColumns,
    isProdHoursPlan = _a.isProdHoursPlan;
  var t = useTranslation('report', {
    keyPrefix: 'period'
  }).t;
  var _b = useFinancialNestedExpansion(data, 'hiringAhead'),
    expansionItems = _b.expansionItems,
    getNestedExpansionItems = _b.getNestedExpansionItems;
  var currency = useSelectCurrency();
  return _jsx(PeriodSectionTable, {
    title: t('hiringAhead', {
      currency: currency
    }),
    data: data,
    index: index,
    addColumns: addColumns,
    isProdHoursPlan: isProdHoursPlan,
    expansionItems: expansionItems,
    defaultExpanded: false,
    tableBodyComponent: HiringAheadBody,
    getNestedExpansionItems: getNestedExpansionItems
  });
};
export var OvertimeCostsTable = function (_a) {
  var data = _a.data,
    index = _a.index,
    addColumns = _a.addColumns,
    isProdHoursPlan = _a.isProdHoursPlan;
  var t = useTranslation('report', {
    keyPrefix: 'period'
  }).t;
  var _b = useFinancialNestedExpansion(data, 'overtimeCosts'),
    expansionItems = _b.expansionItems,
    getNestedExpansionItems = _b.getNestedExpansionItems;
  var currency = useSelectCurrency();
  return _jsx(PeriodSectionTable, {
    title: t('reportRows.overtimeCosts', {
      currency: currency
    }),
    data: data,
    index: index,
    addColumns: addColumns,
    isProdHoursPlan: isProdHoursPlan,
    expansionItems: expansionItems,
    defaultExpanded: false,
    tableBodyComponent: OvertimeCostsBody,
    getNestedExpansionItems: getNestedExpansionItems
  });
};
export var CostPerWorkTable = function (_a) {
  var data = _a.data,
    index = _a.index,
    addColumns = _a.addColumns,
    isProdHoursPlan = _a.isProdHoursPlan;
  var t = useTranslation('report', {
    keyPrefix: 'period'
  }).t;
  var queueExpansionItems = useQueueExpansionItems(data, 'costPerWork');
  var lobExpansionItems = useLobExpansionItems(data, 'costPerWork');
  var multiskillExpansionItems = React.useMemo(function () {
    return getMultiskillExpansionKeys(data.queues, 'costPerWork');
  }, [data.queues]);
  var multiskillGroupExpansionItems = React.useMemo(function () {
    return getMultiskillGroupExpansionKeys(data.queues, 'costPerWork');
  }, [data.queues]);
  var getNestedExpansionItems = useLobNestedExpansionItems(data, 'costPerWork');
  var getCostPerWorkNestedExpansionItems = React.useCallback(function (itemName) {
    if (itemName === getMultiskillSectionExpansionKey('costPerWork')) return multiskillGroupExpansionItems;
    return getNestedExpansionItems(itemName);
  }, [getNestedExpansionItems, multiskillGroupExpansionItems]);
  var expansionItems = React.useMemo(function () {
    return __spreadArray(__spreadArray(__spreadArray([], lobExpansionItems, true), queueExpansionItems, true), multiskillExpansionItems, true);
  }, [lobExpansionItems, multiskillExpansionItems, queueExpansionItems]);
  var currency = useSelectCurrency();
  return _jsx(PeriodSectionTable, {
    title: t('reportRows.costPerWork', {
      currency: currency
    }),
    data: data,
    index: index,
    addColumns: addColumns,
    isProdHoursPlan: isProdHoursPlan,
    expansionItems: expansionItems,
    defaultExpanded: false,
    tableBodyComponent: CostPerWorkBody,
    getNestedExpansionItems: getCostPerWorkNestedExpansionItems
  });
};